<template>
  <Layout class="search">
    <transition name="search">
      <HeaderRoot
        title="Search"
        :class="view?.headerClass"
        class="search__header"
      />
    </transition>

    <div class="layout__wrapper">
      <v-wait for="Error Loading Search">
        <template v-slot:waiting>
          <LoadingError />
        </template>
      </v-wait>
      <v-wait
        :class="[
          'layout__wrapper-inner',
          { 'layout__wrapper-inner--search-results': !!view?.search },
        ]"
        for="Loading Search"
      >
        <template v-slot:waiting>
          <LoadingSpinner />
        </template>
        <div
          v-if="view"
          class="search-box"
          :class="{ 'search-box--results': !view.hasFocus || view.search }"
        >
          <div class="search">
            <div
              class="search__top-box"
              :class="{ 'search__top-box--result': view.search }"
            >
              <div class="container">
                <h1 class="search__title">Search</h1>
                <div class="search__form-box">
                  <div class="search__form-group">
                    <div class="search__input-box">
                      <span class="search__icon iconfont isearch"></span>
                      <!--
                        We disable composing on @input below to fix an issue with Gboard. 
                        Gboard keeps composing enabled mid-word for autoprediction, 
                        but we force-disable it as we intend to do a search on every keypress
                        See https://github.com/shortformhq/main/issues/1879
                        -->
                      <form action="" @submit.prevent.stop="formSubmit">
                        <InputWithContextMenu
                          ref="searchInput"
                          v-model="view.search"
                          class="search__input"
                          :placeholder="view.prompt"
                          type="search"
                          @keydown="view.clearSearchOnEscape($event)"
                          @input="$event.target.composing = false"
                          @focus="view.toggleFocus()"
                          @blur="view.toggleFocus()"
                        />
                        <div
                          v-show="view.search"
                          class="search__reset"
                          @click="view.search = ''"
                        >
                          <span
                            class="search__reset-icon iconfont iclose-1"
                            type="reset"
                          ></span>
                        </div>
                      </form>
                    </div>
                    <span
                      v-if="!view.hasFocus || view.search"
                      class="search__cancel-button"
                      @click="view.search = ''"
                      >Cancel</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <v-wait v-if="view" for="Tags">
          <template v-slot:waiting>
            <TagsBlockSkeleton
              v-if="view.showTags"
              class="container search__skeleton search__skeleton--tags"
            />
          </template>
          <CategoriesSelection
            v-if="view.showTags && view.hasTags()"
            :view="view.tagsView"
            :class="[
              'container',
              { search__categories: !view.hasFocus || view.search },
            ]"
          />
        </v-wait>

        <v-wait v-if="view" for="Books">
          <template v-slot:waiting>
            <BooksBlockSkeleton
              class="container search__skeleton search__skeleton--books"
            />
          </template>
          <template v-if="showBooksSkeleton">
            <BooksBlockSkeleton
              class="container search__skeleton search__skeleton--books"
            />
          </template>
          <BooksBlock
            v-if="showBooksBlock && view.booksListView"
            :view="view.booksListView"
            class="search__books container"
          />
        </v-wait>

        <v-wait v-if="view" for="Articles">
          <template v-slot:waiting>
            <ArticlesBlockSkeleton
              class="container search__skeleton search__skeleton--articles"
            />
          </template>
          <ArticlesBlock
            v-if="view.articlesListView"
            :view="view.articlesListView"
            class="search__articles container"
          />
        </v-wait>

        <v-wait v-if="view" for="Podcasts">
          <template v-slot:waiting>
            <PodcastsBlockSkeleton
              class="container search__skeleton search__skeleton--podcasts"
            />
          </template>
          <PodcastsBlock
            v-if="view.podcastsListView"
            class="container"
            :view="view.podcastsListView"
          />
        </v-wait>

        <v-wait v-if="view" for="Episodes">
          <template v-slot:waiting>
            <PodcastEpisodesBlockSkeleton
              class="container search__skeleton search__skeleton--episodes"
            />
          </template>
          <EpisodesBlock
            v-if="view.episodesListView"
            class="container"
            :view="view.episodesListView"
          />
        </v-wait>

        <EmptyPodcastsBlock
          v-if="view && view.podcastsView && view.showEmptyPodcastBlock"
          :view="view.podcastsView"
          :term="view.search"
        />

        <section v-if="view && view.showEmptyBlock" class="search__no-results">
          <NoSearchResults :term="view.search" />
        </section>
      </v-wait>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'
import type { ComponentPublicInstance, Raw } from 'vue'
import { useRoute } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import Layout from '@/app/components/Layout.vue'
import CategoriesSelection from '@/app/components/listing/categories/CategoriesSelection.vue'
import BooksBlock from '@/app/components/listing/BooksBlock.vue'
import ArticlesBlock from '@/app/components/listing/ArticlesBlock.vue'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import LoadingError from '@/components/ui/LoadingError.vue'

import { Sentry } from '@/services/sentry'

import { SearchView } from '@/models/search'

import { assertNotNull } from '@/helpers/typing'
import HeaderRoot from '@/app/components/header/Root.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import NoSearchResults from '@/app/components/doc/search/NoSearchResults.vue'
import { setFocus } from '@/helpers/form'
import { platform } from '@/services/platform'
import EpisodesBlock from '@/app/components/listing/EpisodesBlock.vue'
import PodcastsBlock from '@/app/components/listing/PodcastsBlock.vue'

import BooksBlockSkeleton from '@/app/components/listing/skeleton-loaders/BooksBlockSkeleton.vue'
import ArticlesBlockSkeleton from '@/app/components/listing/skeleton-loaders/ArticlesBlockSkeleton.vue'
import PodcastsBlockSkeleton from '@/app/components/listing/skeleton-loaders/PodcastsBlockSkeleton.vue'
import PodcastEpisodesBlockSkeleton from '@/app/components/listing/skeleton-loaders/PodcastEpisodesBlockSkeleton.vue'
import TagsBlockSkeleton from '@/app/components/listing/skeleton-loaders/TagsBlockSkeleton.vue'

import EmptyPodcastsBlock from '@/app/components/listing/EmptyPodcastsBlock.vue'
import { backend } from '@/services/backend'

const route = useRoute()
const wait = useWait()

const searchInput = ref<InstanceType<typeof InputWithContextMenu> | null>(null)

const view = ref<Raw<SearchView> | null>(null)
const isInitialLoad = ref(true)

const showBooksSkeleton = computed(() => {
  if (!view.value) {
    return false
  }
  return !!view.value.search && (wait.is('Books') || view.value.isSearching)
})

const showBooksBlock = computed(() => {
  if (!view.value?.booksListView) {
    return false
  }
  if (!view.value.search) {
    return true
  }
  return !wait.is('Books') && !view.value.isSearching
})

const publicInstance = {
  wait,
  // We have to cast the type here because Vue unwraps
  // refs when they are passed to reactive elements (such as
  // `view`, which is where this object is used).
  searchInput: searchInput as any as ComponentPublicInstance | null,
}

onBeforeMount(async () => {
  wait.start('Loading Search')

  await loadSearch()

  // Autofocus search field on Desktop
  // We don't focus on mobile because soft keyboard takes a lot of space
  if (platform.isDesktopWidth() && searchInput.value) {
    nextTick(() => {
      setFocus(searchInput.value?.$el)
    })
  }

  // Initialize event listeners in `nextTick`, after all elements are rendered.
  nextTick(() => {
    // We don't have view initialized if loadSearch() failed.
    if (view.value) {
      view.value.initEventListeners()
    }
  })
})

onBeforeUnmount(() => {
  view.value?.clearEventListeners()
})

watch(
  () => view.value?.search,
  (newSearch, oldSearch) => {
    // Only set isSearching on user input, not on initial load
    if (view.value && newSearch !== oldSearch && !isInitialLoad.value) {
      view.value.isSearching = !!newSearch
    }
    return assertNotNull(view.value).searchHandler()
  },
)

async function loadSearch(): Promise<void> {
  try {
    const searchTerm = route.query.term as string

    const docsUserData = await backend.getDocsUserData()
    view.value = new SearchView(publicInstance, searchTerm, docsUserData)

    await view.value.init()

    // Mark initial load as complete
    isInitialLoad.value = false

    wait.end('Loading Search')
  } catch (error: any) {
    wait.start('Error Loading Search')
    Sentry.captureException(error)
  }
}

/**
 * Submit handler.
 *
 * We need to wrap an input into <form> to have a special
 * "Search" version of the soft keyboard on mobile.
 *
 * When user submits the form (clicks "Search" on soft keyboard)
 * we need to hide the keyboard.
 */
function formSubmit(): void {
  searchInput.value?.$el.blur()
}
</script>

<style scoped lang="scss">
.categories__option {
  position: relative;
}

.wrapper {
  @media only screen and (max-width: $w-tablet-s) {
    min-height: calc(100vh - 64px);
  }
  @media only screen and (max-width: $w-medium-s) {
    min-height: calc(100vh - 64px);
  }
}

.search__books,
.search__articles {
  margin-bottom: 60px;
  @media only screen and (max-width: $w-tablet-s) {
    margin-bottom: 40px;
  }
}

.categories-box {
  margin-block: 40px;

  @media only screen and (max-width: $w-tablet-s) {
    margin-block: 25px;
  }
}

.listing-block {
  margin-top: 40px;

  @media only screen and (max-width: $w-tablet-s) {
    margin-top: 25px;
  }
}
</style>
